import React from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, List, ListItemButton, ListItemText, Stack, Switch, Typography,
} from '@mui/material';
import { stargraveShipUpgrades } from '../../data/stargrave/shipUpgrades';

interface StargraveShipUpgradeModalProps {
  open: boolean;
  availableCredits: number;
  installedUpgradeIds: string[];
  onClose: () => void;
  onPurchase: (upgradeId: string, addForFree: boolean) => void;
}

export const StargraveShipUpgradeModal: React.FC<StargraveShipUpgradeModalProps> = ({
  open,
  availableCredits,
  installedUpgradeIds,
  onClose,
  onPurchase,
}) => {
  const [selectedId, setSelectedId] = React.useState('');
  const [addForFree, setAddForFree] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const selected = stargraveShipUpgrades.find((upgrade) => upgrade.id === selectedId);

  const reset = () => { setSelectedId(''); setAddForFree(false); setError(null); };
  const handleClose = () => { reset(); onClose(); };
  const handlePurchase = () => {
    if (!selected) return setError('Select an upgrade.');
    if (!addForFree && selected.cost > availableCredits) return setError('Not enough credits.');
    onPurchase(selected.id, addForFree);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Install Ship Upgrade</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography color="text.secondary">Available: {availableCredits}cr</Typography>
          <FormControlLabel control={<Switch checked={addForFree} onChange={(event) => setAddForFree(event.target.checked)} />} label="Install for free" />
        </Stack>
        <List sx={{ maxHeight: 390, overflowY: 'auto', border: 1, borderColor: 'divider' }}>
          {stargraveShipUpgrades.map((upgrade) => {
            const installedCount = installedUpgradeIds.filter((id) => id === upgrade.id).length;
            const maximumReached = installedCount >= upgrade.maximum;
            return <ListItemButton key={upgrade.id} selected={selectedId === upgrade.id}
              disabled={maximumReached || (!addForFree && upgrade.cost > availableCredits)}
              onClick={() => { setSelectedId(upgrade.id); setError(null); }}>
              <ListItemText primary={upgrade.name}
                secondary={`${upgrade.cost}cr${installedCount > 0 ? ` | Installed ${installedCount}/${upgrade.maximum}` : ''}`} />
            </ListItemButton>;
          })}
        </List>
        {selected && <React.Fragment>
          <Typography variant="h6">{selected.name}</Typography>
          <Typography variant="body2">{selected.summary}</Typography>
          <Typography variant="caption" color="text.secondary">Core book p. {selected.sourcePage}</Typography>
        </React.Fragment>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handlePurchase} disabled={!selected}>Install</Button>
      </DialogActions>
    </Dialog>
  );
};
